import { api, isUnconfirmedDeliveryError } from "@/lib/api";
import { db } from "@/lib/database";
import {
  AWAITING_CONFIRMATION_MESSAGE,
  isSavedAwaitingConfirmation,
} from "@/lib/orderSubmissionOutcome";
import type { OrderPayload, QueuedOrder } from "@/types/domain";

/**
 * Queued orders are only ever submitted by an explicit, per-order user action
 * (syncQueuedOrder). Auto-resyncing whenever connectivity flaps is what created
 * duplicate orders, so nothing here runs in the background.
 */

export interface CreateOrderResult {
  success: boolean;
  mode: "online" | "queued" | "awaiting_confirmation";
  orderId?: string;
  bcSalesOrderNo?: string;
  queueId?: string;
  queuePosition?: number;
  message: string;
}

type QueueReason = "offline" | "slow_response" | "awaiting_confirmation";

async function queueOrder(
  payload: OrderPayload,
  reason: QueueReason = "offline",
): Promise<CreateOrderResult> {
  const awaiting = reason === "awaiting_confirmation";
  try {
    const now = Date.now();
    const queued: QueuedOrder = {
      queueId: crypto.randomUUID(),
      payload,
      status: "pending",
      retryCount: 0,
      lastError: awaiting ? AWAITING_CONFIRMATION_MESSAGE : undefined,
      createdAt: now,
      updatedAt: now,
    };
    await db.queue.add(queued);
    const queuePosition = await db.queue.count();
    const message = awaiting
      ? AWAITING_CONFIRMATION_MESSAGE
      : reason === "slow_response"
        ? "Your order is taking longer than expected to process. It's been safely queued — please review and sync it from the Order Queue screen."
        : `Order queued. Please sync it manually from the Order Queue screen when you're back online. Queue position: ${queuePosition}`;
    return {
      success: true,
      mode: awaiting ? "awaiting_confirmation" : "queued",
      orderId: payload.orderId,
      queueId: queued.queueId,
      queuePosition,
      message,
    };
  } catch (error) {
    // The backend receipt already guarantees persistence. A local cache failure
    // must not turn it into a fresh checkout or invite a replacement order.
    if (awaiting) {
      return {
        success: true,
        mode: "awaiting_confirmation",
        orderId: payload.orderId,
        message: AWAITING_CONFIRMATION_MESSAGE,
      };
    }
    throw error instanceof Error ? error : new Error("Failed to queue order.");
  }
}

export async function createOrder(
  payload: OrderPayload,
): Promise<CreateOrderResult> {
  // One stable orderId for every retry of this order, so the backend can
  // recognise a retried request instead of treating it as a new order.
  const order: OrderPayload = payload.orderId
    ? payload
    : { ...payload, orderId: crypto.randomUUID() };

  if (!navigator.onLine) return queueOrder(order);

  try {
    const response = await api.ordering.createOrder(order);
    if (isSavedAwaitingConfirmation(response, order.orderId))
      return queueOrder(order, "awaiting_confirmation");
    if (!response.success)
      throw new Error(response.message || "Order creation failed.");
    return {
      success: true,
      mode: "online",
      orderId: response.data?.orderId,
      bcSalesOrderNo: response.data?.bcSalesOrderNo,
      message: response.message || "Order created successfully.",
    };
  } catch (error) {
    // Timeout or dropped connection: the server may well have received it.
    // Queue it with its orderId so a manual sync can check before resubmitting.
    if (isUnconfirmedDeliveryError(error)) return queueOrder(order, "slow_response");
    throw error;
  }
}

/**
 * Manually sync a single queued order. Checks the backend for an existing order
 * with the same orderId first, so a request that succeeded server-side but whose
 * response was lost isn't created twice.
 */
export async function syncQueuedOrder(
  queueId: string,
): Promise<CreateOrderResult> {
  if (!navigator.onLine)
    return {
      success: false,
      mode: "queued",
      queueId,
      message:
        "Cannot sync while offline. Please check your connection and try again.",
    };

  const queued = await db.queue.get(queueId);
  if (!queued)
    return {
      success: false,
      mode: "queued",
      queueId,
      message: "This order is no longer in the queue.",
    };
  await db.queue.update(queueId, { status: "syncing", updatedAt: Date.now() });

  try {
    const orderId = queued.payload.orderId;
    if (!orderId)
      throw new Error(
        "Queued order has no orderId - cannot safely check backend status before resubmitting.",
      );

    const status = await api.ordering.orderStatus(orderId);
    if (status.exists) {
      const bcSalesOrderNo = status.order?.bcSalesOrderNo || undefined;
      // Saved but not confirmed: never resend, keep the local record.
      if (!bcSalesOrderNo && status.order?.status !== "PENDING_CUSTOMER_RESPONSE") {
        await db.queue.update(queueId, {
          status: "pending",
          lastError: AWAITING_CONFIRMATION_MESSAGE,
          updatedAt: Date.now(),
        });
        return {
          success: true,
          mode: "awaiting_confirmation",
          queueId,
          orderId,
          message: AWAITING_CONFIRMATION_MESSAGE,
        };
      }
      await db.queue.delete(queueId);
      return {
        success: true,
        mode: "queued",
        queueId,
        orderId,
        bcSalesOrderNo,
        message: bcSalesOrderNo
          ? `Order was already received. BC Order #${bcSalesOrderNo}`
          : "Order was already received and is being processed.",
      };
    }

    const response = await api.ordering.createOrder(queued.payload);
    if (isSavedAwaitingConfirmation(response, orderId)) {
      await db.queue.update(queueId, {
        status: "pending",
        lastError: AWAITING_CONFIRMATION_MESSAGE,
        updatedAt: Date.now(),
      });
      return {
        success: true,
        mode: "awaiting_confirmation",
        queueId,
        orderId,
        message: AWAITING_CONFIRMATION_MESSAGE,
      };
    }
    if (!response.success)
      throw new Error(response.message || "API returned failure");

    await db.queue.delete(queueId);
    const bcOrderNo = response.data?.bcSalesOrderNo || response.data?.orderId || "unknown";
    return {
      success: true,
      mode: "queued",
      queueId,
      orderId,
      bcSalesOrderNo: response.data?.bcSalesOrderNo,
      message: `Order synced successfully! BC Order #${bcOrderNo}`,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    await db.queue.update(queueId, {
      status: "failed",
      retryCount: queued.retryCount + 1,
      lastError: message,
      updatedAt: Date.now(),
    });
    return { success: false, mode: "queued", queueId, message };
  }
}

export async function removeQueuedOrder(queueId: string): Promise<void> {
  await db.queue.delete(queueId);
}

/** Orders saved on this device that haven't been synced to the backend yet. */
export async function getPendingOrderCount(): Promise<number> {
  try {
    return await db.queue.count();
  } catch (error) {
    console.error("Failed to read the order queue.", error);
    return 0;
  }
}

/** Logout confirmation that warns when unsynced orders are saved on this device. */
export async function confirmLogout(): Promise<boolean> {
  const pending = await getPendingOrderCount();
  const message =
    pending > 0
      ? `You have ${pending} order${pending === 1 ? "" : "s"} waiting to sync. They'll stay saved on this device and sync once you're back online and logged in. Logout now?`
      : "Are you sure you want to logout?";
  return window.confirm(message);
}
